import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = "/api";

// Attach the logged-in token to every API request that doesn't already set one (server enforces role access)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  const url = config.url || "";
  if (token && url.includes("/api/") && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("token");
    const loginType = localStorage.getItem("loginType"); // "user" or "student"
    
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Use different endpoint based on login type
      const endpoint = loginType === "student" ? `${API}/student-portal/me` : `${API}/auth/me`;
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      localStorage.removeItem("token");
      localStorage.removeItem("loginType");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Staff login - form-data with retry and native fetch fallback
  const login = async (email, password) => {
    const maxRetries = 2;
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const params = new URLSearchParams();
        params.append('email', email);
        params.append('password', password);
        
        let data;
        if (attempt === 1) {
          // First try: axios with form-data
          const response = await axios.post(`${API}/auth/login`, params, { timeout: 15000 });
          data = response.data;
        } else {
          // Retry: native fetch (different CORS handling)
          const res = await fetch(`${API}/auth/login`, {
            method: 'POST',
            body: params,
          });
          if (!res.ok) {
            const errBody = await res.json().catch(() => ({}));
            const err = new Error(errBody.detail || `HTTP ${res.status}`);
            err.response = { status: res.status, data: errBody };
            throw err;
          }
          data = await res.json();
        }
        
        const { token, user: userData } = data;
        localStorage.setItem("token", token);
        localStorage.setItem("loginType", "user");
        setUser(userData);
        return userData;
      } catch (error) {
        lastError = error;
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          throw error;
        }
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 1500));
        }
      }
    }
    throw lastError;
  };

  // Student login - uses form-data (URLSearchParams) to bypass CORS preflight for Turkey
  const studentLogin = async (username, password) => {
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // URLSearchParams sends as application/x-www-form-urlencoded
        // This is a "simple request" - no CORS preflight OPTIONS call needed
        const params = new URLSearchParams();
        params.append('username', username);
        params.append('password', password);
        
        const response = await axios.post(`${API}/auth/student-login`, params, {
          timeout: 15000
        });
        const { token, user: userData } = response.data;
        localStorage.setItem("token", token);
        localStorage.setItem("loginType", "student");
        setUser(userData);
        return userData;
      } catch (error) {
        lastError = error;
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          throw error;
        }
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, attempt * 1500));
        }
      }
    }
    throw lastError;
  };

  // Student registration - uses form-data to bypass CORS preflight for Turkey
  const studentRegister = async (name, username, password, referralCode = "", email = "") => {
    const maxRetries = 3;
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const params = new URLSearchParams();
        params.append('name', name);
        params.append('username', username);
        params.append('password', password);
        if (email) params.append('email', email);
        if (referralCode) params.append('referral_code', referralCode);
        
        const response = await axios.post(`${API}/auth/student-register`, params, {
          timeout: 15000
        });
        const { token, user: userData } = response.data;
        localStorage.setItem("token", token);
        localStorage.setItem("loginType", "student");
        setUser(userData);
        return userData;
      } catch (error) {
        lastError = error;
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          throw error;
        }
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, attempt * 1500));
        }
      }
    }
    throw lastError;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("loginType");
    setUser(null);
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, studentLogin, studentRegister, logout, getAuthHeader, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function useAuthHeader() {
  const { getAuthHeader } = useAuth();
  return getAuthHeader();
}
